import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Hero from "@/components/ui/Hero";
import CTABlock from "@/components/ui/CTABlock";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Star } from "lucide-react";

function Stars({ rating }) {
    return (
        <div className="flex items-center gap-0.5" aria-label={`${rating} of 5 stars`}>
            {[1, 2, 3, 4, 5].map((n) => (
                <Star
                    key={n}
                    size={18}
                    fill={n <= rating ? "var(--color-cta)" : "transparent"}
                    color="var(--color-cta)"
                    strokeWidth={1.5}
                />
            ))}
        </div>
    );
}

export default function Reviews() {
    useDocumentTitle(
        "Customer Reviews · A1 Screening · Naples, FL",
        "Verified customer reviews for A1 Screening. We only publish reviews we have verified.",
    );
    const [data, setData] = useState({ reviews: [], count: 0, average_rating: null });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/reviews/public")
            .then((r) => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    return (
        <>
            <Hero
                eyebrow="Customer Reviews"
                title="What Customers Say"
                subtitle="A1 Screening only publishes reviews we have verified. If you've worked with us, we'd love to hear from you."
            />
            <section className="a1-section">
                <div className="a1-container">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                        <div>
                            {data.count > 0 ? (
                                <p className="text-sm" style={{ color: "var(--color-text-muted)" }} data-testid="reviews-summary">
                                    {data.count} verified review{data.count === 1 ? "" : "s"}
                                    {data.average_rating ? ` · ${data.average_rating} / 5 average` : ""}
                                </p>
                            ) : null}
                        </div>
                        <Link to="/leave-a-review" className="a1-btn-secondary" data-testid="reviews-leave-cta">
                            Leave a review
                        </Link>
                    </div>

                    {loading ? (
                        <p style={{ color: "var(--color-text-muted)" }}>Loading…</p>
                    ) : data.reviews.length === 0 ? (
                        <div className="a1-card a1-empty-card" data-testid="reviews-empty-state">
                            <span className="a1-empty-icon" aria-hidden="true">
                                <Star size={22} strokeWidth={1.5} />
                            </span>
                            <p className="m-0 max-w-md" style={{ color: "var(--color-text-muted)" }}>
                                Verified customer reviews will appear here as they are confirmed by A1 Screening. If you've worked with us recently, we'd love to hear from you.
                            </p>
                            <Link to="/leave-a-review" className="a1-btn-secondary" data-testid="reviews-empty-leave-cta">
                                Leave a review
                            </Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reviews-grid">
                            {data.reviews.map((r) => (
                                <article
                                    key={r.id}
                                    className="a1-card"
                                    data-testid={`review-card-${r.id}`}
                                >
                                    <Stars rating={r.rating} />
                                    <p className="mt-3 text-base" style={{ color: "var(--color-text)" }}>
                                        “{r.review_text}”
                                    </p>
                                    <div className="mt-4 text-sm" style={{ color: "var(--color-text-muted)" }}>
                                        <div className="font-semibold" style={{ color: "var(--color-text)" }}>
                                            — {r.customer_name}
                                        </div>
                                        {r.service_type || r.city_or_area ? (
                                            <div className="text-xs mt-0.5 uppercase tracking-wider" style={{ color: "var(--color-text-dim)" }}>
                                                {[r.service_type, r.city_or_area].filter(Boolean).join(" · ")}
                                            </div>
                                        ) : null}
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </section>
            <CTABlock />
        </>
    );
}
